import { Injectable, computed, signal } from '@angular/core';

import { GONDOLA_COOKIE, SESSION_USER_KEY } from '../config/auth.config';
import { decodeJwtPayload, getCookie } from '../utils/auth.util';

/** Roles de usuario interno (ver CLAUDE.md §Autenticación y acceso). */
export type Rol = 'Tesoreria' | 'TIC' | 'Contabilidad';

export interface Usuario {
  nombre: string;
  email: string;
  rol: Rol;
}

/** Usuario de desarrollo/demo cuando no hay sesión del gateway (mismo criterio que billing-engine). */
const USUARIO_DEMO: Usuario = {
  nombre: 'Laura Torres',
  email: 'laura.torres@isimo.com',
  rol: 'TIC',
};

function sinAcentos(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** Primer string no vacío entre varias llaves candidatas del objeto `auth_user`. */
function texto(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Mapea el claim `rol` del JWT de Góndola a un rol del panel. `null` si no lo reconoce. */
function rolDesdeClaim(raw: unknown): Rol | null {
  if (typeof raw !== 'string') return null;
  const r = sinAcentos(raw).toLowerCase().trim();
  if (r === 'tesoreria') return 'Tesoreria';
  if (r === 'tic') return 'TIC';
  if (r === 'contabilidad') return 'Contabilidad';
  return null;
}

/**
 * Identidad de la sesión: usuario de `sessionStorage.auth_user` (lo deja el gateway) y rol
 * del claim `rol` del JWT en la cookie `gondola_session`. Sin sesión → usuario demo, para
 * no romper dev. Tolera ausencia, JSON inválido y doble codificación.
 */
function leerUsuario(): Usuario {
  let nombre = '';
  let email = '';
  try {
    const raw = sessionStorage.getItem(SESSION_USER_KEY);
    if (raw) {
      let parsed: unknown = JSON.parse(raw);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed); // por si viene doble-encodeado
      if (parsed && typeof parsed === 'object') {
        const o = parsed as Record<string, unknown>;
        nombre = texto(o, 'fullName', 'full_name', 'nombre', 'name', 'displayName');
        email = texto(o, 'email', 'correo', 'mail');
      }
    }
  } catch {
    /* sesión ausente o inválida */
  }

  const claim = decodeJwtPayload<{ rol?: unknown }>(getCookie(GONDOLA_COOKIE));
  const rol = rolDesdeClaim(claim?.rol);

  if (!nombre && !email && !rol) return USUARIO_DEMO;
  return {
    nombre: nombre || USUARIO_DEMO.nombre,
    email: email || USUARIO_DEMO.email,
    rol: rol ?? USUARIO_DEMO.rol,
  };
}

const NOTA_ROL: Record<Rol | 'Todos', string> = {
  Todos:
    'Rol mixto — panel completo de vigilancia, seguimiento, elegibilidad y conciliación de la integración de factoring.',
  Tesoreria:
    'Vista Tesorería — énfasis en conciliación de desembolsos y en que cada cesión tenga su reclasificación.',
  TIC: 'Vista Área TIC — énfasis en disponibilidad del servicio, reintentos y alertas de integración.',
  Contabilidad:
    'Vista Contabilidad — énfasis en los asientos de reclasificación proveedor→factor y cuadre de montos.',
};

/**
 * Autenticación del panel. La identidad (`usuario`) se lee de la sesión del gateway de
 * Góndola (cookie `gondola_session` + `sessionStorage.auth_user`); el token lo valida el
 * gateway y el `authInterceptor` fuerza re-login en 401. Sin sesión cae al usuario demo.
 *
 * Aparte de la identidad, el selector de rol del topbar cambia el ÉNFASIS de la vista
 * (Tesorería / TIC / Contabilidad), como en el diseño Góndola; `null` = rol mixto ("Todos").
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Usuario autenticado (sesión del gateway) o demo si no hay sesión. */
  readonly usuario = signal<Usuario>(leerUsuario());

  /** Rol de la vista seleccionado en el topbar; `null` = Todos. */
  readonly rolVista = signal<Rol | null>(null);

  readonly esTic = computed(() => this.rolVista() === 'TIC' || this.rolVista() === null);
  readonly notaRol = computed(() => NOTA_ROL[this.rolVista() ?? 'Todos']);

  setRolVista(rol: Rol): void {
    // volver a pulsar el rol activo lo deselecciona (vuelve a "Todos").
    this.rolVista.update((actual) => (actual === rol ? null : rol));
  }

  tieneRol(roles?: Rol[]): boolean {
    if (!roles || roles.length === 0) return true;
    const r = this.rolVista();
    return r == null || roles.includes(r);
  }

  /** Re-lee la sesión del gateway (cookie + `sessionStorage`) sin recargar la página. */
  refrescarSesion(): void {
    this.usuario.set(leerUsuario());
  }
}
